import { FilterMenu, FilterOption } from 'dls-react-filters';
import React from 'react';
import { FilterSubMenuViewModel } from 'src/types/view-models/transactions/filter-sub-menu.view.model';
import { TransactionFilterMenu } from 'src/types/transaction-filter-menu.enum';
import AccountService from 'src/services/account.service';
import { AccountModel } from 'src/types/account.model';
import Selectors from 'src/utils/selectors';
import useFilteredOptions from 'src/hooks/useFilteredOptions';
import { CategoryModel } from 'src/types/category.model';
import styles from './TransactionFilter.module.scss';
import LiteralsService from 'src/services/literals/LiteralsService';

export default function FilterSubMenu({
    parentFilterId,
    menuKey,
    menuName,
    showMenu,
    options,
    onClose,
    onResetButtonClick,
    selectedOptions,
    handleOptionClick,
    align,
    searchable
}: FilterSubMenuViewModel): JSX.Element {
    const { searchProps, filteredOptions, handleFilter } = useFilteredOptions({ options });
    const isAccountMenu = menuKey === TransactionFilterMenu.ACCOUNT;
    const isCategoryMenu = menuKey === TransactionFilterMenu.CATEGORY;
    const selectedOptionsIndex = options.findIndex((option) => option.id === selectedOptions[menuKey]?.id);
    const focusedOptionIndex = searchable || selectedOptionsIndex >= 0 ? selectedOptionsIndex : 0;
    // need this due to the difference in the way events are attached in react 16 (DLS)
    // vs react 17 (bw-react)
    if (!showMenu) return null;

    return (
        <FilterMenu
            id={`${menuKey}-filter`}
            parentFilterId={parentFilterId}
            showMenu={showMenu}
            data-cy={Selectors.Transactions.FILTER_SUB_MENU}
            onClose={() => {
                onClose();
                handleFilter();
            }}
            onResetButtonClick={() => {
                onResetButtonClick();
                handleFilter();
            }}
            filtersLabel={`Filter by: ${menuName}`}
            singleSelect
            focusedOptionIndex={focusedOptionIndex}
            searchProps={searchable ? searchProps : undefined}
            subMenu
            align={align}
            mobileGutterWidth="44px"
        >
            {filteredOptions.length ? (
                filteredOptions.map((option) => {
                    const name = isAccountMenu ? AccountService.getDisplayName(option as AccountModel) : option.name;

                    if (isCategoryMenu && !(option as CategoryModel).parentId) {
                        const OptionIcon = (option as CategoryModel).icon;
                        return (
                            <FilterOption
                                key={option.id}
                                isSelected={selectedOptions[menuKey]?.id === option.id}
                                // id of AccountModel is a number, rather than a string like the rest of the ids
                                id={option.id.toString()}
                                onClick={() => {
                                    handleOptionClick(menuKey, option);
                                    handleFilter();
                                }}
                                withIcons
                                icon={OptionIcon && <OptionIcon />}
                                className={styles.filterOption}
                                tabIndex={0}
                            >
                                <div id={`description-${name}`} className="sr-only">
                                    This is the start of the {name} section.
                                </div>
                                {name}
                            </FilterOption>
                        );
                    }

                    return (
                        <FilterOption
                            key={option.id}
                            isSelected={selectedOptions[menuKey]?.id === option.id}
                            // id of AccountModel is a number, rather than a string like the rest of the ids
                            id={option.id.toString()}
                            onClick={() => {
                                handleOptionClick(menuKey, option);
                                handleFilter();
                            }}
                            withIcons={isCategoryMenu}
                            className={styles.filterOption}
                        >
                            {name}
                        </FilterOption>
                    );
                })
            ) : (
                <li className={styles.noSearchResults} role="option" aria-selected="false" aria-disabled="true">
                    {searchProps.value
                        ? LiteralsService.getLiteral('General.search.noResultsFor', [searchProps.value])
                        : LiteralsService.getLiteral('General.search.noResults')}
                </li>
            )}
        </FilterMenu>
    );
}
